import { css } from '@emotion/react';
import { IonButton, IonIcon, IonText } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { downloadOutline } from 'ionicons/icons';
import React from 'react';
import { fetchMeasurementCsv } from '../../api/queries/getDatasourceMeasurementsCsv';
import { useSettings } from '../../contexts/SettingsContext';
import { Spinner } from '../Spinner';

const DownloadMeasurements: React.FC = () => {
  const { marker, networkId } = useSettings();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleDownload = React.useCallback(async () => {
    if (!marker?.datasourceId || !networkId) {
      return;
    }

    setIsLoading(true);
    try {
      const csvText = await fetchMeasurementCsv(queryClient, marker.datasourceId, networkId);

      // Create a blob to specify the MIME type of the data
      const blob = new Blob([csvText], { type: 'text/csv' });

      // Create a temporary URL so the browser can download the file
      const url = URL.createObjectURL(blob);

      // Start the download process by creating a link and clicking it
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${marker.datasourceName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, marker?.datasourceId, networkId]);

  const downloadMeasurementsCss = {
    container: css({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    }),
    spinner: [css({ marginRight: '0.5rem' })],
  };

  return (
    <div css={downloadMeasurementsCss.container}>
      <IonText>Get the last 30 days of measurements.</IonText>
      <IonButton onClick={handleDownload} disabled={isLoading || !networkId || !marker?.datasourceId}>
        {isLoading ? (
          <div css={downloadMeasurementsCss.spinner} slot="start">
            <Spinner type="circular" size="1rem" />
          </div>
        ) : (
          <IonIcon icon={downloadOutline} slot="start" />
        )}
        Download
      </IonButton>
    </div>
  );
};

export default DownloadMeasurements;
